import { BulkConversationActionResponse as BulkConversationActionResponseSchema } from '@wit-support/admin-contracts';
import { ModuleRequestError, createModuleApi, readErrorCode, unwrapCoded, } from '@wit-support/admin-client-core';
const api = createModuleApi({ errorFormat: 'raw' });
export function setConversationsApiBaseUrl(url) {
    api.setBaseUrl(url);
}
/**
 * Exposed so sibling modules (e.g. the inline template picker fetch in the
 * reply composer) can prefix the same base URL the rest of the module uses.
 */
export function conversationsApiBaseUrl() {
    return api.baseUrl();
}
/**
 * Thrown from `getConversation` when the server responds HTTP 303 with a
 * `{ mergedInto }` body — the conversation was merged into another.
 * Callers should catch and navigate to the target id.
 */
export class ConversationMergedError extends Error {
    /**
     * The surviving conversation id, or `null` when the merge was detected but the
     * target id is UNREADABLE (a 303 whose body lacked/garbled `mergedInto`, or an
     * `opaqueredirect` the runtime followed across origins). Callers MUST guard
     * navigation on a non-null `targetId` and fall back to a refresh hint otherwise
     * — but every path is still recognized as a merge (so idempotency-key handling
     * is uniform) rather than degrading to a generic Error.
     */
    targetId;
    constructor(targetId) {
        super(targetId ? `conversation merged into ${targetId}` : 'conversation merged (target unknown)');
        this.name = 'ConversationMergedError';
        this.targetId = targetId;
    }
}
/**
 * Inspect a `redirect: 'manual'` response for the merge-tombstone 303 the
 * conversation routes emit (`{ mergedInto }` JSON body + `Location` header). On
 * a 303 this ALWAYS throws — `ConversationMergedError(targetId)` when the body
 * carries the target, else a generic "merged but no target id" error. An
 * `opaqueredirect` (the runtime silently followed the 303 — non-same-origin)
 * also throws, with a refresh hint since the target id is unreadable. Returns
 * `void` for any non-redirect response so the caller proceeds normally.
 *
 * Centralizes the exact branch `postReply` / `updateConversationStatus` use so
 * every mutating/reading call against a possibly-merged conversation surfaces
 * the merge identically instead of silently degrading a POST/DELETE to a GET
 * (transparent-redirect default) on a 303.
 */
async function throwIfMergedRedirect(r) {
    if (r.status === 303) {
        try {
            const data = (await r.json());
            if (data.mergedInto) {
                throw new ConversationMergedError(data.mergedInto);
            }
        }
        catch (err) {
            if (err instanceof ConversationMergedError)
                throw err;
            // Fall through — body wasn't JSON.
        }
        // Recognized as a merge, but the target id is unreadable. Throw the MERGE
        // error (target null) — NOT a generic Error — so the caller's merge handling
        // (clear idempotency key, refresh hint) fires uniformly instead of treating
        // it as an ambiguous 5xx and preserving a now-misdirected key.
        throw new ConversationMergedError(null);
    }
    if (r.type === 'opaqueredirect') {
        // Non-same-origin 303 the runtime silently followed — same as above, target
        // unreadable but it IS a merge. Surface it as such, not a generic Error.
        throw new ConversationMergedError(null);
    }
}
export async function listConversations(filter = {}) {
    const params = new URLSearchParams();
    for (const c of filter.channels ?? [])
        params.append('channel', c);
    for (const id of filter.channelAccountIds ?? [])
        params.append('channelAccountId', id);
    for (const s of filter.statuses ?? [])
        params.append('status', s);
    for (const t of filter.topicKeys ?? [])
        params.append('topic', t);
    for (const id of filter.tagIds ?? [])
        params.append('tag', id);
    if (filter.from)
        params.set('from', filter.from);
    if (filter.to)
        params.set('to', filter.to);
    if (filter.q)
        params.set('q', filter.q);
    if (filter.contactId)
        params.set('contactId', filter.contactId);
    if (filter.cursor)
        params.set('cursor', filter.cursor);
    if (filter.limit !== undefined)
        params.set('limit', String(filter.limit));
    if (filter.hasScheduledEmail !== undefined)
        params.set('hasScheduledEmail', String(filter.hasScheduledEmail));
    if (filter.awaitingOurReply !== undefined)
        params.set('awaitingOurReply', String(filter.awaitingOurReply));
    if (filter.isWhale !== undefined)
        params.set('isWhale', String(filter.isWhale));
    if (filter.hasChargeback !== undefined)
        params.set('hasChargeback', String(filter.hasChargeback));
    if (filter.hasComment !== undefined)
        params.set('hasComment', String(filter.hasComment));
    const qs = params.toString();
    const r = await fetch(`${api.baseUrl()}/api/conversations${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Lightweight listing of `channel_account` rows for the inbox filter.
 * Duplicates a slice of `@wit-support/admin-module-channels` intentionally:
 * admin-module-conversations can't import from another admin module
 * (eslint-boundaries), and we only need id/name/kind.
 */
export async function listInboxChannels() {
    const r = await fetch(`${api.baseUrl()}/api/channels`, { credentials: 'include' });
    if (!r.ok) {
        // Don't throw — the inbox stays usable without the channel filter list.
        return [];
    }
    const data = (await r.json());
    const rows = Array.isArray(data) ? data : (data.channels ?? []);
    return rows.map((c) => ({ id: c.id, name: c.name, kind: c.kind }));
}
/**
 * Fetch the classification topic taxonomy (`GET /api/topics`) for the inbox
 * topic filter. Returns the active taxonomy as a flat array. Fail-soft: returns
 * `[]` on error so the inbox stays usable without the topic filter.
 */
export async function listTopics() {
    const r = await fetch(`${api.baseUrl()}/api/topics`, { credentials: 'include' });
    if (!r.ok)
        return [];
    return (await r.json());
}
export async function getConversation(id) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${id}`, {
        credentials: 'include',
        // Prevent the browser from transparently following the 303 — we want to
        // surface the merge to the caller.
        redirect: 'manual',
    });
    if (r.status === 303) {
        // With `redirect: 'manual'` the body is readable for 303.
        try {
            const data = (await r.json());
            if (data.mergedInto) {
                throw new ConversationMergedError(data.mergedInto);
            }
        }
        catch (err) {
            if (err instanceof ConversationMergedError)
                throw err;
            // Fall through — body wasn't JSON.
        }
        throw new Error('conversation merged but server did not include target id');
    }
    // `opaqueredirect` can appear when the runtime silently converted the 303
    // even under manual mode (e.g. non-same-origin). Treat as a merge miss and
    // fall back to a standard fetch so callers still get usable data.
    if (r.type === 'opaqueredirect') {
        const r2 = await fetch(`${api.baseUrl()}/api/conversations/${id}`, {
            credentials: 'include',
        });
        return api.unwrap(r2);
    }
    return api.unwrap(r);
}
export async function mergeConversation(sourceId, input) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${sourceId}/merge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Thrown by {@link mergeAllConversations} on ANY `409` from the server. Every
 * conflict the route answers — `preview_stale`, `already_merged`,
 * `different_contact` — means the same thing to the dialog: the set of
 * conversations it is showing no longer matches reality and NOTHING was
 * merged, so the preview must be refetched before the operator confirms again.
 * `code` is the wire error code, for the toast wording only.
 *
 * A dedicated class with the code as data, not a string match on the message:
 * the dialog reacts to a conflict differently from every other failure
 * (refetch + stay open instead of just surfacing the error), and matching on
 * wire text would break the moment a message is reworded.
 */
export class MergeAllConflictError extends Error {
    code;
    constructor(code) {
        super(`merge-all conflict: ${code}`);
        this.code = code;
        this.name = 'MergeAllConflictError';
    }
}
/** The one conflict with its own toast — the set the operator confirmed changed. */
export class MergePreviewStaleError extends MergeAllConflictError {
    constructor() {
        super('preview_stale');
        this.name = 'MergePreviewStaleError';
    }
}
/**
 * What «слить все в этот» would do — computed server-side, nothing written.
 * With `sourceIds` the PLAN is computed for that subset (the sources list
 * stays the full live set), so the dialog can describe exactly the rows the
 * operator has checked. Sent as repeated `source` query params.
 */
export function getMergePreview(targetId, sourceIds) {
    const qs = sourceIds?.length
        ? `?${sourceIds.map((id) => `source=${encodeURIComponent(id)}`).join('&')}`
        : '';
    return api.get(`/api/conversations/${targetId}/merge-preview${qs}`);
}
/**
 * Merge the confirmed set of the contact's conversations into `targetId`.
 * `sourceIds` is echoed back to the server, which compares it against a FRESH
 * preview and refuses a stale confirmation.
 */
export async function mergeAllConversations(targetId, input) {
    const r = await api.fetchRaw(`/api/conversations/${targetId}/merge-all`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
    });
    if (!r.ok) {
        // Read the body ONCE: `extractServerError` consumes it, so the stale check
        // has to happen on the parsed error code it returns.
        const msg = await api.extractServerError(r);
        if (r.status === 409) {
            throw msg === 'preview_stale' ? new MergePreviewStaleError() : new MergeAllConflictError(msg);
        }
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * POST a reply to a conversation. Body carries `{ subject?, bodyHtml, bodyText? }`
 * — the editor produces HTML and the server derives a plain-text fallback when
 * omitted. Returns the serialized outbound MessageDTO on 201. Throws
 * `ConversationMergedError` on 303 (the source was tombstoned and merged into
 * another conversation — caller should navigate to the target). Any other
 * non-2xx surfaces as `Error(msg)` with the server error body preserved for
 * toast display.
 */
export async function postReply(id, input, options = {}) {
    const headers = { 'content-type': 'application/json' };
    if (options.idempotencyKey)
        headers['idempotency-key'] = options.idempotencyKey;
    const r = await fetch(`${api.baseUrl()}/api/conversations/${id}/reply`, {
        method: 'POST',
        credentials: 'include',
        redirect: 'manual',
        headers,
        body: JSON.stringify(input),
    });
    if (r.status === 303) {
        try {
            const data = (await r.json());
            if (data.mergedInto) {
                throw new ConversationMergedError(data.mergedInto);
            }
        }
        catch (err) {
            if (err instanceof ConversationMergedError)
                throw err;
        }
        throw new Error('conversation merged but server did not include target id');
    }
    if (r.type === 'opaqueredirect') {
        throw new Error('conversation merged — refresh to continue');
    }
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
export async function translateMessage(conversationId, messageId, targetLang = 'ru') {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/messages/${messageId}/translation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ targetLang }),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Manually trigger a reclassification of a conversation using the LLM
 * classifier. Scope determines whether to classify only the first message
 * or all messages in the conversation. Returns 202 (accepted) when the
 * reclassification is queued. Throws on non-ok response.
 */
export async function reclassifyConversation(conversationId, scope) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/reclassify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scope }),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
}
/**
 * Manually override the classifier's `senderCapacity` verdict for a
 * conversation ('customer' | 'business'), or clear the override (`null`)
 * to fall back to the model's own decision again. Fire-and-forget: the
 * server writes the column + a status-history-style event and emits the
 * outbox events the worker re-gates on asynchronously — the 202 body
 * carries `{ accepted, from, to }` for callers that want the transition,
 * but the effect only lands once the caller polls `getConversation`.
 */
export async function overrideSenderCapacity(conversationId, value) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/sender-capacity`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ value }),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Batch fetch all cached translations for a conversation at a given target
 * language. Used by the thread UI to seed the per-message translation map in
 * a single round-trip instead of N individual GETs. Defaults to 'ru'.
 */
export async function listConversationTranslations(conversationId, targetLang = 'ru') {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/translations?targetLang=${encodeURIComponent(targetLang)}`, {
        credentials: 'include',
        // Surface a merge tombstone (303) instead of transparently following it to
        // the OTHER conversation's translations.
        redirect: 'manual',
    });
    await throwIfMergedRedirect(r);
    return api.unwrap(r);
}
/**
 * Blacklist the conversation's sender email. The server upserts the address
 * into `email_blacklist` and hides all of that contact's conversations
 * (`blocked_at`). Returns `{ email, blocked }`; `email` is null (and
 * `blocked` is 0) when the contact has no email — a graceful no-op.
 */
export async function blacklistConversation(conversationId, reason) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/blacklist`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Fetch the subscription/billing summary for the conversation's contact
 * (wit-payment-service). Fail-soft on the server: `configured:false` when
 * payment is unwired, `summary:null` when there's no resolved user / billing
 * record / on a payment outage. Money fields are in cents — format in the UI.
 */
export async function getConversationSubscription(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/subscription`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Fetch the retaining-funnel action history for the conversation's user.
 * Fail-soft on the server: `configured:false` when payment is unwired and
 * `actions:[]` when there's no resolved user / no records / an upstream outage.
 */
/**
 * Кейсы платёжного риска клиента этого диалога (rail-панель «Чарджбеки»).
 * Гейт — `chargebacks.read`, тот же ключ, что у раздела: панель не зовёт этот
 * метод без права, иначе каждое открытие чата давало бы 403.
 */
export async function getConversationChargebacks(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/chargebacks`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Stripe customer id(s) billing resolved for this conversation's contact
 * (rail's `ContactCard` + «Найти клиента» panel). Gated `subscription.read` —
 * same key as the billing/subscription rail, since this IS billing data.
 * Fail-soft on the server for «nothing to search» (`ids: []`); a partial
 * upstream search reports `unavailable: true` (also `ids: []`, but NOT to be
 * read as «no Stripe id known» — the caller must hide the Stripe line rather
 * than assert a negative), and a genuine upstream OUTAGE is a 502 that
 * `useQuery` surfaces as an error, not a thrown empty list.
 */
export async function getStripeCustomers(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/stripe-customers`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
export async function listSubscriptionActions(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/subscription-actions`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Fetch the soulmate-portrait links for the conversation's contact. Fail-soft on
 * the server: `configured:false` when portraits are unwired and `portraits:[]`
 * when there's no resolved user / no portraits. URLs are copy-only in the UI.
 */
export async function listUserPortraits(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/portraits`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Fetch the product-usage stats for the conversation's contact. Fail-soft on
 * the server: `configured:false` when the usage API is unwired and `usage:null`
 * when there's no resolved user / no usage data.
 */
export async function listProductUsage(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/product-usage`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Cancel every active subscription for the conversation's contact. Idempotent
 * upstream (`alreadyCancelled` counts as success). Returns `{ ok:false, reason }`
 * when there's no resolved user / payment isn't configured, else the aggregate.
 */
export async function cancelConversationSubscription(conversationId, reason) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/cancel-subscription`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Eligible pipelines an operator may MANUALLY run for this conversation: those
 * whose trigger expression matched the conversation's classification labels.
 * INCLUDES disabled pipelines (`enabled === false`) — a manual run is still
 * allowed even when auto runs are off. Empty list when the conversation is
 * unclassified or nothing matches.
 */
export async function listEligiblePipelines(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/pipelines`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Thrown by `runPipeline` when the server responds 409 `pipeline_not_eligible`
 * — the pipeline key is no longer in the conversation's eligible set (e.g. the
 * classification changed, or the pipeline trigger was edited). Callers surface
 * a distinct "больше не подходит" message and should refetch the list.
 */
export class PipelineNotEligibleError extends Error {
    key;
    constructor(key) {
        super(`pipeline ${key} is no longer eligible`);
        this.key = key;
        this.name = 'PipelineNotEligibleError';
    }
}
/**
 * Thrown by `runPipeline` when the server responds 409 `pipeline_already_running`
 * — a run of this pipeline is still in-flight for the conversation (e.g. a
 * double-click while the first run hasn't reached a terminal state). The pipeline
 * IS still eligible; the caller should surface a truthful message and NOT
 * refetch the eligible list.
 *
 * `reason` splits the two blockers the server lumps under one code, because the
 * operator's next move differs: a `running` run is seconds away from finishing,
 * while a `waiting` one is parked on the CUSTOMER and may sit until
 * `waitDeadline` (up to 48 h). Both are optional — an older admin-api sends
 * neither, and the caller then falls back to the generic wording.
 */
export class PipelineAlreadyRunningError extends Error {
    key;
    reason;
    waitDeadline;
    constructor(key, reason = null, waitDeadline = null) {
        super(`pipeline ${key} is already running`);
        this.key = key;
        this.reason = reason;
        this.waitDeadline = waitDeadline;
        this.name = 'PipelineAlreadyRunningError';
    }
}
/**
 * Manually trigger a specific (incl. disabled) pipeline for the conversation.
 * Returns the 202 ack on success. On 409 reads the error body and throws
 * `PipelineAlreadyRunningError` (`pipeline_already_running`) or
 * `PipelineNotEligibleError` (`pipeline_not_eligible`); any other non-2xx throws
 * a generic `Error` (with the server error body). The run executes
 * asynchronously in the worker; the conversation card re-renders from DB state
 * as the run progresses.
 */
export async function runPipeline(conversationId, pipelineKey) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/pipelines/${encodeURIComponent(pipelineKey)}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
    });
    if (r.status === 409) {
        const body = (await r.json().catch(() => ({})));
        if (body.error === 'pipeline_already_running') {
            throw new PipelineAlreadyRunningError(pipelineKey, body.reason === 'waiting_for_customer' || body.reason === 'running' ? body.reason : null, body.waitDeadline ?? null);
        }
        // Default any other 409 (incl. the canonical `pipeline_not_eligible`) to the
        // not-eligible error so the caller refetches the eligible list.
        throw new PipelineNotEligibleError(pipelineKey);
    }
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
/**
 * Fetch the manual + auto pipeline RUN history for a conversation
 * (`GET /api/conversations/:id/pipelines/runs`). Returned newest-first; each
 * run carries its name, lifecycle status, and timing. `finishedAt`/`durationMs`
 * are null while a run is still in-flight.
 */
export async function listConversationPipelineRuns(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/pipelines/runs`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Per-step detail for a SINGLE pipeline run on a conversation. Backs the
 * expandable run rows in «История запусков» — fetched on demand when a run is
 * expanded. Conversation-scoped on the server (404 when the run id does not
 * belong to the conversation). Steps come ordered by `seq`.
 */
export async function getConversationPipelineRunDetail(conversationId, runId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/pipelines/runs/${runId}`, { credentials: 'include' });
    return api.unwrap(r);
}
/**
 * Manually change a conversation's status. PATCHes the conversation with an
 * `UpdateConversationRequest` body; the server persists the new status (and may
 * emit a status-changed event). Returns the refreshed `ConversationDTO`. Throws
 * `ConversationMergedError` on 303 (the conversation was tombstoned + merged —
 * caller should navigate to the target), else a generic `Error` on non-2xx.
 *
 * The second argument is the whole REQUEST, not a bare status, because a status
 * change may carry an operator note (`comment`) that the server stores as an
 * internal comment linked to the resulting status-history row. Passing the
 * request object keeps the client signature identical to the contract, so a new
 * optional field never needs another positional parameter here.
 */
export async function updateConversationStatus(conversationId, body) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}`, {
        method: 'PATCH',
        credentials: 'include',
        redirect: 'manual',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (r.status === 303) {
        try {
            const data = (await r.json());
            if (data.mergedInto)
                throw new ConversationMergedError(data.mergedInto);
        }
        catch (err) {
            if (err instanceof ConversationMergedError)
                throw err;
        }
        throw new Error('conversation merged but server did not include target id');
    }
    // `opaqueredirect` can appear when the runtime silently converted the 303 even
    // under manual mode (e.g. non-same-origin) — we can't read the target id, so
    // surface the merge with a refresh hint rather than a confusing generic error.
    // Mirrors the `postReply` path.
    if (r.type === 'opaqueredirect') {
        throw new Error('conversation merged — refresh to continue');
    }
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return (await r.json());
}
// ---------------------------------------------------------------------------
// Scheduled emails
// ---------------------------------------------------------------------------
export async function listScheduledEmails(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/scheduled-emails`, {
        credentials: 'include',
        // The server 303-redirects a merge tombstone to the live target. Without
        // `redirect: 'manual'` the browser transparently FOLLOWS the same-origin
        // 303 and returns the MERGE TARGET's queue, which ScheduledQueuePanel would
        // render under the tombstoned source's rail with no merge indication —
        // showing another conversation's scheduled emails. Mirror the sibling
        // calls (createScheduledEmail / cancelScheduledEmail / listConversation-
        // Translations): surface the redirect as ConversationMergedError instead.
        redirect: 'manual',
    });
    await throwIfMergedRedirect(r);
    // Server responds `{ items: ScheduledEmailDTO[] }`.
    const body = await api.unwrap(r);
    return body.items;
}
export async function createScheduledEmail(conversationId, body, options = {}) {
    const headers = { 'content-type': 'application/json' };
    if (options.idempotencyKey)
        headers['idempotency-key'] = options.idempotencyKey;
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/scheduled-emails`, {
        method: 'POST',
        credentials: 'include',
        // Surface a merge tombstone (303) — without this the POST transparently
        // follows the redirect, silently degrading to a GET (no schedule created)
        // that would otherwise look like a success.
        redirect: 'manual',
        headers,
        body: JSON.stringify(body),
    });
    await throwIfMergedRedirect(r);
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        // Carry the HTTP status so the caller can distinguish a DEFINITIVE rejection
        // (4xx — the server did NOT create the row) from an AMBIGUOUS failure (5xx —
        // the row MAY have been created before the error). The composer preserves the
        // idempotency key on ambiguous failures so a retry collapses onto the
        // possibly-created row instead of creating a SECOND scheduled email.
        throw new ScheduledEmailRequestError(msg, r.status);
    }
    return (await r.json());
}
/** Thrown by `createScheduledEmail` on a non-OK response; carries the HTTP status. */
export class ScheduledEmailRequestError extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = 'ScheduledEmailRequestError';
    }
}
export async function cancelScheduledEmail(conversationId, scheduledEmailId) {
    // Server route is `DELETE /api/conversations/:id/scheduled-emails/:sid`
    // (no `/cancel` segment) — cancellation is the DELETE verb.
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/scheduled-emails/${scheduledEmailId}`, {
        method: 'DELETE',
        credentials: 'include',
        // Surface a merge tombstone (303) — without this the DELETE transparently
        // follows the redirect, degrading to a GET (nothing cancelled) that would
        // look like a success.
        redirect: 'manual',
    });
    await throwIfMergedRedirect(r);
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
}
/**
 * Fetch the status-change history for a conversation
 * (`GET /api/conversations/:id/status-history`), read from `conversation_event`
 * rows with `event_type='status_changed'`. Returned newest-first per the server
 * contract (`created_at DESC`); backs the «История статусов» popup in the
 * conversation rail.
 */
export async function getStatusHistory(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/status-history`, {
        credentials: 'include',
    });
    if (!r.ok)
        throw new Error('Не удалось загрузить историю статусов');
    const body = (await r.json());
    return body.entries;
}
export async function listMessages(conversationId, cursor) {
    const params = new URLSearchParams();
    if (cursor)
        params.set('cursor', cursor);
    const qs = params.toString();
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`, { credentials: 'include' });
    return api.unwrap(r);
}
// ---------------------------------------------------------------------------
// Bulk actions
// ---------------------------------------------------------------------------
/**
 * Fan ONE action (`change_status` / `run_pipeline` / `send_reply` /
 * `schedule_email`) out over many conversations in a single request. The server
 * is best-effort: one conversation failing never aborts the rest. Returns a
 * per-conversation result list plus an aggregate `summary`. HTTP 200 even on
 * partial failures (per-row detail in `results`); a non-2xx (400 bad body / 503
 * unconfigured dependency) surfaces as `Error(msg)` with the server error body
 * preserved for toast display. The response is zod-parsed so a shape drift
 * surfaces here rather than as a downstream `undefined` access.
 */
export async function bulkConversationAction(body, opts) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/bulk-action`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'content-type': 'application/json',
            // A stable, client-pinned token so a retry of the SAME bulk attempt (e.g.
            // after an ambiguous network failure) reuses the server-side idempotency
            // keys for `send_reply`/`schedule_email` and never double-sends. Omitted →
            // the server falls back to its per-request id (safe, but not retry-proof).
            ...(opts?.idempotencyKey ? { 'idempotency-key': opts.idempotencyKey } : {}),
        },
        body: JSON.stringify(body),
    });
    if (!r.ok) {
        const msg = await api.extractServerError(r);
        throw new Error(msg);
    }
    return BulkConversationActionResponseSchema.parse(await r.json());
}
/**
 * Prefill for the identity-search panel — the four digits the customer already
 * quoted (from the newest pipeline run) and the contact's display name. Never
 * fails the panel: the server degrades to `{last4:null,name:null}` rather than
 * erroring, because a missing prefill is a typing inconvenience.
 */
export async function getIdentitySearchHints(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/identity/search-hints`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Search billing for the customer behind this conversation. The response is a
 * discriminated union, NOT a list: «the upstream search route is not deployed»
 * and «we searched and nobody matches» must reach the operator as different
 * sentences. A genuine upstream failure is the only case that throws (502).
 *
 * Empty fields are dropped rather than sent blank — the server treats `?email=`
 * as a malformed query, and an all-empty query is answered `empty_query`.
 */
export async function searchIdentityCandidates(conversationId, query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        const trimmed = v?.trim();
        if (trimmed)
            params.set(k, trimmed);
    }
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/identity/search?${params.toString()}`, { credentials: 'include' });
    return api.unwrap(r);
}
/**
 * Bind the chosen candidate's WIT user to this conversation's contact.
 *
 * 409 (already bound to somebody else) and 404 (no contact) are RESULTS, not
 * transport errors — they carry the body the panel needs to explain itself — so
 * they are returned rather than thrown. `unwrap` is deliberately not used here.
 */
export async function attachIdentity(conversationId, witUserId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/identity/attach`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ witUserId }),
    });
    if (r.status === 409 || r.status === 404) {
        return (await r.json());
    }
    return api.unwrap(r);
}
// ---------------------------------------------------------------------------
// Теги диалога
// ---------------------------------------------------------------------------
/**
 * Справочник тегов под подсказки поля ввода. `q` — подстрока имени; сервер сам
 * решает, что считать совпадением, и ранжирует выдачу, поэтому клиент НЕ
 * фильтрует ответ повторно (см. комментарий в `TagInput`).
 *
 * Пустые поля выкидываются, а не уходят пустыми: `?q=` — это «ищи пустую
 * строку», а не «ничего не искал», и часть роутов считает такой запрос
 * некорректным.
 */
export async function listTags(query = {}) {
    const params = new URLSearchParams();
    const q = query.q?.trim();
    if (q)
        params.set('q', q);
    if (query.limit != null)
        params.set('limit', String(query.limit));
    const suffix = params.toString();
    const r = await fetch(`${api.baseUrl()}/api/tags${suffix ? `?${suffix}` : ''}`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
/**
 * Создать тег в общем словаре. Рейлу диалога он НЕ нужен — `PUT …/tags`
 * создаёт недостающие имена сам, одной транзакцией; отдельный вызов оставлен
 * для экрана управления словарём.
 */
export async function createTag(body) {
    const r = await fetch(`${api.baseUrl()}/api/tags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    return api.unwrap(r);
}
/**
 * Полная замена набора тегов диалога (не дельта). Отправляются ИМЕНА, а не
 * идентификаторы: тег, набранный оператором впервые, ещё не существует, и
 * сервер заводит недостающие в той же транзакции.
 */
export async function setConversationTags(conversationId, body) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/tags`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    return unwrapCoded(r);
}
// ---------------------------------------------------------------------------
// Внутренние комментарии диалога
// ---------------------------------------------------------------------------
export async function listComments(conversationId) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/comments`, {
        credentials: 'include',
    });
    return api.unwrap(r);
}
export async function addComment(conversationId, body) {
    const r = await fetch(`${api.baseUrl()}/api/conversations/${conversationId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    return unwrapCoded(r);
}
/**
 * Изменить тело комментария. `created_at` остаётся, сервер ставит
 * `updated_at` + `edited_by`; право — автор или `comment.manage`, гейт на
 * сервере, клиент лишь не показывает пункт меню тем, кому нельзя.
 */
export async function updateComment(commentId, body) {
    const r = await fetch(`${api.baseUrl()}/api/comments/${commentId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    return unwrapCoded(r);
}
/**
 * Удалить комментарий. Ответ — 204 БЕЗ тела, поэтому `unwrap` здесь не годится:
 * он попытался бы разобрать пустое тело как JSON и упал бы на успешном ответе.
 */
export async function deleteComment(commentId) {
    const r = await fetch(`${api.baseUrl()}/api/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    if (!r.ok) {
        throw new ModuleRequestError(await readErrorCode(r), r.status);
    }
}
//# sourceMappingURL=api.js.map